// Auth service configuration + JWT helpers (FRD-OXBO-002 consumer side)
//
// The SPA authenticates against the oxbo-auth service (auth.oxbo.energy in
// PROD, auth.dev/auth.qa per env) rather than the biotk-api. The biotk-api
// only *validates* the resulting JWT (via oxbo-auth's JWKS); it does not issue
// tokens. See FRD-OXBO-002 §RF-7 / §RF-13.

import type { Role } from './types';

/**
 * The 10 valid BIOT-k roles (must match the Role union in ./types and the keys
 * of ROLE_PERMISSIONS in ./constants). oxbo-auth org_roles/system_roles are
 * free-form strings on the wire; we validate against this set so an unknown
 * role from the IdP cannot become a verbatim, permission-less role string
 * (which would silently lock the user out of every action).
 */
const KNOWN_ROLES: ReadonlySet<string> = new Set<Role>([
  'owner',
  'operator',
  'designer',
  'pm_cm',
  'consultant',
  'driller',
  'inspector',
  'field_clerk',
  'admin',
  'super_admin',
]);

/**
 * Base URL of the oxbo-auth service. Configured per-env at build time via
 * VITE_AUTH_URL (e.g. https://auth.dev.oxbo.energy). When unset, falls back to
 * same-origin relative paths so a fully-local dev stack still works.
 */
export const AUTH_BASE_URL: string = (import.meta.env.VITE_AUTH_URL ?? '').replace(/\/$/, '');

/**
 * Dev-mint bypass flag. Only ever true when VITE_DEV_MINT_MODE is explicitly
 * 'true' AND the build is a Vite dev build. This is belt-and-suspenders:
 * production/QA builds set import.meta.env.PROD = true, so even a stray
 * VITE_DEV_MINT_MODE=true in a prod build cannot re-enable the bypass.
 */
export const DEV_MINT_MODE: boolean =
  import.meta.env.DEV === true && import.meta.env.VITE_DEV_MINT_MODE === 'true';

/** Decoded oxbo-auth access-token claims (flat, no namespace — see issuer.py). */
export interface OxboAuthClaims {
  sub: string;
  org_id?: string;
  org_roles?: string[];
  system_roles?: string[];
  email?: string;
  exp?: number;
  iat?: number;
}

/** Nil UUID — oxbo-auth uses this for org_id when the user has no active org. */
const NIL_UUID = '00000000-0000-0000-0000-000000000000';

/**
 * Decode (without verifying) a JWT payload. Signature verification is the
 * biotk-api's job (it holds the JWKS); the SPA only needs the claims to build
 * the local User object. Returns null on any parse failure.
 */
export function decodeJwtClaims(token: string): OxboAuthClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json) as OxboAuthClaims;
  } catch {
    return null;
  }
}

/**
 * Resolve the primary display Role from the oxbo-auth claims.
 * Precedence: super_admin (system role) > first KNOWN org role > 'consultant'.
 * Unknown role strings from the IdP are rejected (fall through to consultant)
 * so the user never lands on a verbatim, permission-less role.
 */
export function resolveRole(claims: OxboAuthClaims): Role {
  if ((claims.system_roles ?? []).includes('super_admin')) return 'super_admin';
  const orgRole = (claims.org_roles ?? []).find((r) => KNOWN_ROLES.has(r));
  return (orgRole as Role) ?? 'consultant';
}

/** True when the claims carry an active org membership (non-nil org_id). */
export function hasActiveOrg(claims: OxboAuthClaims): boolean {
  return Boolean(claims.org_id) && claims.org_id !== NIL_UUID;
}
